import { useDispatch } from "react-redux";
import axios from "axios";
import { apiConstants, postGeneric } from "../../../common/constants";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { decodeAuthToken, isProzoPasswordLoginBlocked, PROZO_EMAIL_LOGIN_ERROR } from "../../../helpers/UtilityHelper";
import { login, loginUserDataSlice } from "../../../redux/userSlice";
import { fetchReportData } from "../../../redux/actions/reportAction";

export const useLoginLogic = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { enqueueSnackbar } = useSnackbar();

    const handlePhoneLogin = async (mobileNumber) => {
        const payload = {
            mobileNumber,
            panel: "MERCHANT"
        };
        try {
            const res = await postGeneric(apiConstants.MOBILE_LOGIN, payload);
            if (res.status === 200 && res?.data?.status === "success") {
                navigate('/verify-otp', { state: { mobileNumber, mfaTokenId: res.data?.tokenId } });
                enqueueSnackbar(res?.data?.message, { variant: 'success' });
            } else {
                enqueueSnackbar(res?.data?.error, { variant: 'error' });
            }
        } catch (error) {
            console.log(error, 'error');
            enqueueSnackbar(error.response?.data?.error || error.response?.data?.message || "Login failed", {
                variant: "error",
            });
        }
    };

    const handleEmailLogin = async (credentials) => {
        if (isProzoPasswordLoginBlocked(credentials?.username)) {
            enqueueSnackbar(PROZO_EMAIL_LOGIN_ERROR, { variant: "error" });
            return;
        }
        const loginUrl = `${apiConstants.BASE_LOCAL}${apiConstants.AUTH}`;
        try {
            const authResponse = await axios.post(
                loginUrl,
                { ...credentials, panel: "MERCHANT" },
                { headers: { "Content-Type": "application/json" } }
            );
            const { data: dataLogin } = authResponse;
            if (dataLogin.mfaTokenId) {
                enqueueSnackbar('OTP sent successfully!', { variant: 'success' });
                navigate('/otp?type=mfa', { state: { mfaTokenId: dataLogin.mfaTokenId, email: credentials.username } });
            }
            else {
                localStorage.setItem("userToken", dataLogin.accessToken);
                if (dataLogin.refreshToken) localStorage.setItem("refreshToken", dataLogin.refreshToken);
                dispatch(loginUserDataSlice({ ...dataLogin }))
                const { merchantOid, merchantList, roles, switchableMerchantOids } = decodeAuthToken(dataLogin.accessToken);
                const hasSwitchableMerchants = Array.isArray(switchableMerchantOids) && switchableMerchantOids.length > 0;
                if (hasSwitchableMerchants || (roles?.includes('ROLE_PROSHIPADMIN') && (merchantList === undefined || merchantList?.length === 0 || merchantList?.length > 0))) {
                    let url = "/loginuser";
                    navigate(url);
                }
                else {
                    let merchantData;
                    let config = {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + localStorage.getItem("userToken"),
                        },
                    };
                    let merchantUrl =
                        apiConstants.BASE_LOCAL + apiConstants.GET_MERCHANT_SEARCH;
                    const { data } = await axios.post(
                        merchantUrl,
                        {
                            offset: 0,
                            limit: 1,
                            orfilter: [],
                            andfilter: [{ field: "id", operator: "eq", value: merchantOid }],
                            excludes: "mappedOrderRule,sla",
                        },
                        config
                    );
                    merchantData = data.data[0];
                    // delete merchantData.codRemittanceConfig;
                    delete merchantData.mappedOrderRule;
                    delete merchantData.wallet;
                    delete merchantData.pickup_details;
                    delete merchantData.bank_details;
                    delete merchantData.alertSubscription;
                    delete merchantData.gstDetails;
                    delete merchantData.merchantWebHooks;
                    delete merchantData.activeSlas;
                    delete merchantData.alertSubscriptionB2B;
                    const userInfo = { ...dataLogin, merchant: merchantData };
                    dispatch(login(userInfo));
                    dispatch(fetchReportData(merchantOid));
                }
            }
        } catch (e) {
            console.error(e, 'asdjavjdvajsdvasdbv');
            if (e.response?.status === 401) {
                enqueueSnackbar("Invalid username or password", { variant: "error" });
            } else if (e.response.status === 403) {
                enqueueSnackbar(e.response?.data || "Access Denied", { variant: "error" });
            } else {
                enqueueSnackbar(e.response.data?.error || e.response.data?.message || "Login failed", {
                    variant: "error",
                });
            }
        }
    };

    const handleGoogleSignIn = () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI;
        const scope = process.env.REACT_APP_GOOGLE_SCOPE;
        const responseType = 'code';
        const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&access_type=offline`;
        window.location.href = googleUrl;
    };


    // MSAL LOGIN
    const generateRandomString = (length = 64) => {
        const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
        let result = "";
        const randomValues = window.crypto.getRandomValues(new Uint32Array(length));
        for (let i = 0; i < length; i++) {
            result += charset[randomValues[i] % charset.length];
        }
        return result;
    };

    const sha256 = async (plain) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(plain);
        const hash = await crypto.subtle.digest("SHA-256", data);
        return btoa(String.fromCharCode(...new Uint8Array(hash)))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    };

    const handleMSALSignIn = async () => {
        const clientId = process.env.REACT_APP_MSAL_CLIENT_ID;
        const redirectUri = process.env.REACT_APP_MSAL_REDIRECT_URI;

        const code_verifier = generateRandomString();
        localStorage.setItem("ms_code_verifier", code_verifier);

        const code_challenge = await sha256(code_verifier);

        const scope = "openid profile email User.Read offline_access";

        const microsoftUrl =
            `https://login.microsoftonline.com/common/oauth2/v2.0/authorize` +
            `?client_id=${clientId}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&scope=${encodeURIComponent(scope)}` +
            `&response_mode=query` +
            `&code_challenge=${code_challenge}` +
            `&code_challenge_method=S256`;

        window.location.href = microsoftUrl;
    };


    return {
        handlePhoneLogin,
        handleEmailLogin,
        handleGoogleSignIn,
        handleMSALSignIn
    };
}; 